import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  runInAction,
  when,
} from 'mobx'

import {
  drawableAdjustRows,
  ExportAdjustRow,
  isPercentRow,
} from '~/client/src/shared/map/exportAdjustGroups'
import { READ_SCALE_BY_SHEET } from '~/client/src/shared/map/exportReadScale'
import {
  ExportPrintRow,
  ExportPrintTextKind,
  ExportSymbolKind,
  ExportTextKind,
  IExportKindValues,
} from '~/client/src/shared/map/exportTextScale'
import {
  ExportOrientation,
  ExportPaperPreset,
  ExportQuality,
  ExportScreenShape,
  ExportSizePreset,
  IExportLayout,
  isPaperPreset,
  resolveExportLayout,
} from '~/client/src/shared/map/mapExportPaper'
import {
  ExportPhase,
  MapExportError,
} from '~/client/src/shared/map/mapExportPlan'
import { ExportBasemap } from '~/client/src/shared/map/mapExportStyle'
import {
  claimKeyboard,
  KeyboardClaim,
  releaseKeyboard,
} from '~/client/src/shared/utils/keyboardClaim'

import {
  DEFAULT_EXPORT_LAYERS,
  IExportLayers,
  NO_WORKFLOW_LAYERS,
} from './exportLayers'
import { beginExportRun, endExportRun } from './exportRunSentinel'
import {
  DEFAULT_EXPORT_SETTINGS,
  defaultPaperFor,
  defaultPrintPaperFor,
  ExportFormat,
  ExportKeyPlace,
  ExportLabelMode,
  ExportSizeKind,
  ExportUseFor,
  IMapExportSettings,
  loadExportSettings,
  saveExportSettings,
} from './mapExportSettings'

// The map is ready within seconds of a reload; later, the user has moved on.
const REOPEN_AFTER_RELOAD_MS = 15_000

export interface IMapExportSeed {
  projectId: string
  viewCss: { width: number; height: number }
  basemap: ExportBasemap
  layers: Partial<IExportLayers>
  markupLabelsVisible: boolean
  // Print opens on A4 rather than Letter. Absent means Letter.
  isMetricProject?: boolean
  // Absent means the page draws Workflows.
  hasWorkflows?: boolean
}

export interface IMapExportEntry {
  isAvailable(): boolean
  guard(proceed: () => void): void
  readSeed(): IMapExportSeed
}

export type ExportTask = (
  signal: AbortSignal,
  onPhase: (phase: ExportPhase) => void,
) => Promise<void>

const isAbortError = (error: unknown): boolean =>
  error instanceof DOMException && error.name === 'AbortError'

// Settings for one export. The settings in IMapExportSettings are remembered per project in this
// browser once an export succeeds with them, so a change never exported is gone on the next open;
// nothing writes back to the live map's style or markup preferences.
// The persisted fields: every one is an observable of the same name on the store.
const SETTINGS_KEYS = Object.keys(DEFAULT_EXPORT_SETTINGS) as Array<
  keyof IMapExportSettings
>

export default class MapExportStore {
  @observable public isOpen = false
  @observable public format = DEFAULT_EXPORT_SETTINGS.format
  @observable public useFor = DEFAULT_EXPORT_SETTINGS.useFor
  @observable public viewPreset = DEFAULT_EXPORT_SETTINGS.viewPreset
  @observable public paper = DEFAULT_EXPORT_SETTINGS.paper
  @observable public isMetricProject = false
  @observable public orientation = DEFAULT_EXPORT_SETTINGS.orientation
  @observable public screenShape = DEFAULT_EXPORT_SETTINGS.screenShape
  @observable public screenOrientation =
    DEFAULT_EXPORT_SETTINGS.screenOrientation
  @observable public quality = DEFAULT_EXPORT_SETTINGS.quality
  @observable public basemap = ExportBasemap.Street
  @observable public layers: IExportLayers = { ...DEFAULT_EXPORT_LAYERS }
  @observable public labelMode = DEFAULT_EXPORT_SETTINGS.labelMode
  @observable public readScale = DEFAULT_EXPORT_SETTINGS.readScale
  @observable public screenPercent = DEFAULT_EXPORT_SETTINGS.screenPercent
  @observable public isKeyIncluded = DEFAULT_EXPORT_SETTINGS.isKeyIncluded
  @observable.ref public textKindPercent =
    DEFAULT_EXPORT_SETTINGS.textKindPercent
  @observable.ref public symbolKindPercent =
    DEFAULT_EXPORT_SETTINGS.symbolKindPercent
  @observable.ref public printTextKindPx =
    DEFAULT_EXPORT_SETTINGS.printTextKindPx
  @observable.ref public printSymbolKindPx =
    DEFAULT_EXPORT_SETTINGS.printSymbolKindPx
  @observable public arePageElementsShown =
    DEFAULT_EXPORT_SETTINGS.arePageElementsShown
  @observable public hasWorkflows = true
  // The live map's size when the export opened: where the frame starts, never the file's size.
  @observable public viewCss = { width: 0, height: 0 }
  @observable public phase: ExportPhase = 'idle'
  @observable.ref public failure: MapExportError | null = null
  @observable public isCancelled = false
  @observable.ref private entry: IMapExportEntry | null = null
  private keyboardClaim: KeyboardClaim | null = null
  private projectId: string | null = null
  private abortController: AbortController | null = null
  private readonly persistsSettings: boolean
  private readonly recordsRuns: boolean
  private readonly defaults: IMapExportSettings
  private savedSettings: IMapExportSettings

  // The phone offers only its own Screen file, so it starts from its own defaults and neither reads
  // nor saves the project's desktop settings: a remembered ARCH D print would pass the phone's caps
  // with no way back. It also records each run, so a page the phone killed mid-export can say so
  // once it reloads.
  public constructor({
    persistsSettings = true,
    recordsRuns = false,
    defaults = DEFAULT_EXPORT_SETTINGS,
  } = {}) {
    makeObservable(this)
    this.persistsSettings = persistsSettings
    this.recordsRuns = recordsRuns
    this.defaults = defaults
    this.savedSettings = defaults
  }

  public dispose(): void {
    this.close()
  }

  @computed
  public get canOpen(): boolean {
    return !!this.entry?.isAvailable()
  }

  // Labels drawn on a layer read off while that layer is off; the choice itself is kept for when
  // it comes back on.
  @computed
  public get shownLayers(): IExportLayers {
    const { layers } = this
    return {
      ...layers,
      workflowLabels: layers.workflowPins && layers.workflowLabels,
      workflowMarkupLabels:
        layers.workflowMarkups && layers.workflowMarkupLabels,
    }
  }

  @computed
  public get layout(): IExportLayout {
    return resolveExportLayout({
      preset: this.sizePreset,
      orientation: this.pageOrientation,
      quality: this.quality,
      screenShape: this.screenShape,
    })
  }

  // Screen and Print each keep their own orientation.
  @computed
  public get pageOrientation(): ExportOrientation {
    return this.sizeKind === ExportSizeKind.Screen
      ? this.screenOrientation
      : this.orientation
  }

  @computed
  public get sizeKind(): ExportSizeKind {
    return this.useFor === 'screen'
      ? ExportSizeKind.Screen
      : ExportSizeKind.Paper
  }

  // The paper Print uses, kept while on Screen.
  @computed
  public get printPaper(): ExportPaperPreset {
    return this.paper ?? defaultPrintPaperFor(this.isMetricProject)
  }

  // The paper the sizes read: on Screen the project's office paper, for the key's place; nothing on
  // Screen prints on it.
  @computed
  public get paperPreset(): ExportPaperPreset {
    return this.sizeKind === ExportSizeKind.Paper
      ? this.printPaper
      : defaultPaperFor(this.isMetricProject)
  }

  @computed
  public get sizePreset(): ExportSizePreset {
    return this.sizeKind === ExportSizeKind.Paper
      ? this.paperPreset
      : this.viewPreset
  }

  // What Adjust by type holds: Print's set sizes, Screen's and Markup text's percents.
  @computed
  public get kindValues(): IExportKindValues {
    return {
      textPercent: this.textKindPercent,
      symbolPercent: this.symbolKindPercent,
      textPx: this.printTextKindPx,
      symbolPx: this.printSymbolKindPx,
    }
  }

  // The rows of the current Use for that are off their default: a set size on Print, a percent off
  // 100 otherwise. Hidden rows count too, so the badge says when rows out of sight change the file;
  // rows the page can never draw (Workflows on Map Setup) do not, as their sizes belong to the
  // Workflows map.
  @computed
  public get isAdjustByTypeCustomized(): boolean {
    return drawableAdjustRows(this.hasWorkflows).some(
      row => !this.isAdjustRowDefault(row),
    )
  }

  // Where the label key goes: page 2 of a PDF, else a strip beside a PNG's map, which on paper
  // makes the file wider than the page.
  @computed
  public get keyPlace(): ExportKeyPlace {
    if (this.format === ExportFormat.Pdf) return 'pageTwo'
    return this.sizeKind === ExportSizeKind.Paper ? 'besidePage' : 'besideMap'
  }

  @computed
  public get isExporting(): boolean {
    return this.phase !== 'idle'
  }

  @computed
  public get settings(): IMapExportSettings {
    return Object.fromEntries(
      SETTINGS_KEYS.map(key => [key, this[key]]),
    ) as unknown as IMapExportSettings
  }

  @action.bound
  public setEntry(entry: IMapExportEntry | null): void {
    this.entry = entry
  }

  @action.bound
  public open(): void {
    const entry = this.entry
    if (!entry?.isAvailable()) {
      return
    }
    // The guard can resume after a network save, by when the page may have left or changed.
    entry.guard(() => {
      if (this.entry === entry && entry.isAvailable()) {
        this.openSeeded(entry.readSeed())
      }
    })
  }

  // After the page died mid-export, Share map reopens saying why once the map can export, but only
  // in the moments after the reload, never later on another view. It never restarts the export
  // itself: a run that killed the page once is the user's to retry.
  public reopenAfterReload(): IReactionDisposer {
    return when(
      () => this.canOpen,
      () => {
        const { entry } = this
        // As in open: the guard can resume after the page left or changed.
        entry.guard(() => {
          if (this.entry !== entry || !entry.isAvailable()) {
            return
          }
          this.openSeeded(entry.readSeed())
          runInAction(() => (this.failure = new MapExportError('pageReloaded')))
        })
      },
      { timeout: REOPEN_AFTER_RELOAD_MS, onError: () => undefined },
    )
  }

  @action.bound
  public close(): void {
    this.cancelExport()
    this.isOpen = false
    releaseKeyboard(this.keyboardClaim)
    this.keyboardClaim = null
  }

  // Esc, the close button and Cancel all stop a running export first, and close only once idle.
  @action.bound
  public dismiss(): void {
    if (this.isExporting) {
      this.cancelExport()
      return
    }
    this.close()
  }

  public async runExport(task: ExportTask): Promise<void> {
    if (this.isExporting) {
      return
    }
    const controller = new AbortController()
    this.abortController = controller
    runInAction(() => {
      this.phase = 'preparing'
      this.failure = null
      this.isCancelled = false
    })

    let failure: MapExportError | null = null
    let isCancelled = false
    // Leaving the page ends the run on purpose; only a page killed outright leaves the record.
    const isRecorded = this.recordsRuns && !!this.projectId
    if (isRecorded) {
      beginExportRun(this.projectId)
      window.addEventListener('pagehide', endExportRun)
    }
    try {
      await task(controller.signal, this.setPhase)
    } catch (error) {
      if (isAbortError(error)) {
        isCancelled = true
      } else {
        failure =
          error instanceof MapExportError ? error : new MapExportError('failed')
      }
    }

    if (isRecorded) {
      window.removeEventListener('pagehide', endExportRun)
      endExportRun()
    }
    if (this.abortController === controller) {
      this.abortController = null
    }
    runInAction(() => {
      this.phase = 'idle'
      this.failure = failure
      this.isCancelled = isCancelled
    })
    if (!failure && !isCancelled) {
      this.keepExported()
    }
  }

  @action.bound
  public cancelExport(): void {
    this.abortController?.abort()
  }

  @action.bound
  public seedSession(seed: IMapExportSeed): void {
    this.viewCss = seed.viewCss
    this.basemap = seed.basemap
    this.hasWorkflows = seed.hasWorkflows ?? true
    this.layers = {
      ...DEFAULT_EXPORT_LAYERS,
      workflowMarkupLabels: seed.markupLabelsVisible,
      ...seed.layers,
      ...(!this.hasWorkflows && NO_WORKFLOW_LAYERS),
    }
    this.isMetricProject = seed.isMetricProject ?? false
    const projectId = seed.projectId || null
    // Every open starts from what was last exported: the project's saved settings, else what this
    // store exported with for the same project (the phone, or storage that reads nothing), else
    // the defaults.
    this.savedSettings =
      (this.persistsSettings && projectId && loadExportSettings(projectId)) ||
      (projectId === this.projectId ? this.savedSettings : this.defaults)
    this.applySettings(this.savedSettings)
    this.projectId = projectId
  }

  @action.bound
  public setFormat(format: ExportFormat): void {
    this.format = format
  }

  @action.bound
  public setUseFor(useFor: ExportUseFor): void {
    this.useFor = useFor
  }

  // A paper is Print's, even while on Screen. Another sheet reads from its own Standard.
  @action.bound
  public setSizePreset(sizePreset: ExportSizePreset): void {
    if (!isPaperPreset(sizePreset)) {
      this.viewPreset = sizePreset
      return
    }
    if (sizePreset !== this.printPaper) {
      this.readScale = READ_SCALE_BY_SHEET[sizePreset].standard
    }
    this.paper = sizePreset
  }

  // A rung past the sheet's maximum reads at the maximum, set by how much of the sheet a name takes
  // and the distance in sheet diagonals, not by fit.
  @action.bound
  public setReadScale(readScale: number): void {
    this.readScale = Math.min(
      readScale,
      READ_SCALE_BY_SHEET[this.printPaper].maximum,
    )
  }

  @action.bound
  public setOrientation(orientation: ExportOrientation): void {
    if (this.sizeKind === ExportSizeKind.Screen) {
      this.screenOrientation = orientation
    } else {
      this.orientation = orientation
    }
  }

  @action.bound
  public setScreenShape(shape: ExportScreenShape): void {
    this.screenShape = shape
  }

  @action.bound
  public setQuality(quality: ExportQuality): void {
    this.quality = quality
  }

  @action.bound
  public setBasemap(basemap: ExportBasemap): void {
    this.basemap = basemap
  }

  @action.bound
  public setLayer(key: keyof IExportLayers, value: boolean): void {
    this.layers[key] = value
  }

  // One action, so a whole group switching re-solves the labels once.
  @action.bound
  public setLayers(keys: Array<keyof IExportLayers>, value: boolean): void {
    keys.forEach(key => {
      this.layers[key] = value
    })
  }

  @action.bound
  public setLabelMode(labelMode: ExportLabelMode): void {
    this.labelMode = labelMode
  }

  @action.bound
  public setScreenPercent(screenPercent: number): void {
    this.screenPercent = screenPercent
  }

  @action.bound
  public setTextKindPercent(kind: ExportTextKind, percent: number): void {
    if (this.textKindPercent[kind] === percent) {
      return
    }
    this.textKindPercent = { ...this.textKindPercent, [kind]: percent }
  }

  @action.bound
  public setSymbolKindPercent(kind: ExportSymbolKind, percent: number): void {
    if (this.symbolKindPercent[kind] === percent) {
      return
    }
    this.symbolKindPercent = { ...this.symbolKindPercent, [kind]: percent }
  }

  // A Print row's printed size, in sheet px; null puts it back on Auto.
  @action.bound
  public setPrintKindPx(row: ExportPrintRow, px: number | null): void {
    if (row.type === 'text') {
      this.printTextKindPx = withSize(this.printTextKindPx, row.kind, px)
    } else {
      this.printSymbolKindPx = withSize(this.printSymbolKindPx, row.kind, px)
    }
  }

  // One row back to its default: Auto on Print, the default percent for a percent row.
  @action.bound
  public resetAdjustRow(row: ExportAdjustRow): void {
    if (!isPercentRow(row, this.sizeKind === ExportSizeKind.Paper)) {
      this.setPrintKindPx(row as ExportPrintRow, null)
    } else if (row.type === 'text') {
      this.setTextKindPercent(row.kind, this.defaults.textKindPercent[row.kind])
    } else {
      this.setSymbolKindPercent(
        row.kind,
        this.defaults.symbolKindPercent[row.kind],
      )
    }
  }

  // Every row of Adjust by type for the current Use for, hidden ones too; the reading distance and
  // Overall size stay, and so do the other Use for's rows.
  @action.bound
  public resetAdjustByType(): void {
    drawableAdjustRows(this.hasWorkflows).forEach(this.resetAdjustRow)
  }

  public isAdjustRowDefault(row: ExportAdjustRow): boolean {
    if (isPercentRow(row, this.sizeKind === ExportSizeKind.Paper)) {
      return row.type === 'text'
        ? this.textKindPercent[row.kind] ===
            this.defaults.textKindPercent[row.kind]
        : this.symbolKindPercent[row.kind] ===
            this.defaults.symbolKindPercent[row.kind]
    }
    return row.type === 'text'
      ? this.printTextKindPx[row.kind as ExportPrintTextKind] === undefined
      : this.printSymbolKindPx[row.kind] === undefined
  }

  @action.bound
  public setKeyIncluded(isKeyIncluded: boolean): void {
    this.isKeyIncluded = isKeyIncluded
  }

  @action.bound
  public setPageElementsShown(isShown: boolean): void {
    this.arePageElementsShown = isShown
  }

  @action.bound
  private setPhase(phase: ExportPhase): void {
    this.phase = phase
  }

  @action.bound
  private openSeeded(seed: IMapExportSeed): void {
    this.seedSession(seed)
    this.failure = null
    this.isCancelled = false
    this.isOpen = true
    // Taken as it opens, not once the preview's code has loaded, so the page beneath never handles
    // a key meant for the export.
    releaseKeyboard(this.keyboardClaim)
    this.keyboardClaim = claimKeyboard()
  }

  // Kept in memory even with nowhere to save it, so a reopen in this session starts from it too.
  private keepExported(): void {
    this.savedSettings = this.settings
    if (this.persistsSettings && this.projectId) {
      saveExportSettings(this.projectId, this.savedSettings)
    }
  }

  @action.bound
  private applySettings(settings: IMapExportSettings): void {
    SETTINGS_KEYS.forEach(key => Object.assign(this, { [key]: settings[key] }))
  }
}

const withSize = <K extends string>(
  sizes: Partial<Record<K, number>>,
  kind: K,
  px: number | null,
): Partial<Record<K, number>> => {
  if ((sizes[kind] ?? null) === px) {
    return sizes
  }
  const next = { ...sizes }
  if (px === null) {
    delete next[kind]
  } else {
    next[kind] = px
  }
  return next
}
